import React, { useState, useEffect, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import DiscardIcon from "../assets/icons/profile_discard.png";
import EditIcon from "../assets/icons/profile_edit.png";
import RedLogoutIcon from "../assets/icons/profile_redlogout.png";
import CameraIcon from "../assets/icons/profile_camera.png";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { resetSidebarState } from "../redux/sidebarSlice";
import {
  showSuccessToast,
  showErrorToast,
  showInfoToast,
} from "../utils/toastUtils";
import "react-toastify/dist/ReactToastify.css";
import { TOAST_MESSAGES } from "../utils/toastMessages";

import api from "../api";
import "../css/Profile.css";
import { triggerRefresh } from "../redux/refreshSlice";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

const UserProfileSettings = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [user, setUser] = useState({
    user_profile_pic: "",
    name: "",
    email: "",
  });

  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activitySessions, setActivitySessions] = useState([]);
  const [nameError, setNameError] = useState(false);

  const [formData, setFormData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const [userRole, setUserRole] = useState(null);

  useEffect(() => {
    if (user) {
      setUserRole(user.role_name || "User");
    }
  }, [user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const [isEditingUserName, setIsEditingUserName] = useState(false);
  const userNameInputRef = useRef(null);

  useEffect(() => {
    if (isEditingUserName && userNameInputRef.current) {
      userNameInputRef.current.focus(); // ⬅️ auto-focus input when editing starts
    }
  }, [isEditingUserName]);

  const token =
    localStorage.getItem("access_token") ||
    sessionStorage.getItem("access_token");

  const fetchActivity = async () => {
    try {
      setLoading(true);
      const { data } = await api.get(
        `${process.env.REACT_APP_API_URL}/auth/activity`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // ✅ Store sessions data from response
      setActivitySessions(data.sessions || []);

      // console.log("Fetched activity sessions:", data.sessions);
    } catch (err) {
      console.error("Failed to fetch activity:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchActivity();
  }, [token]);

  const handleLogoutDevice = async (session_id) => {
    try {
      // Call API to logout single device
      await api.post(
        `${process.env.REACT_APP_API_URL}/auth/logout-device`,
        { session_id }, // ✅ Send correct session ID
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // ✅ Fetch updated session data
      fetchActivity();
    } catch (err) {
      console.error("Logout device failed:", err);
    }
  };

  const handleSave = async () => {
    try {
      // 1. Password update (only if new + confirm fields are filled)
      if (formData.newPassword && formData.confirmPassword) {
        const passwordResponse = await fetch(
          `${process.env.REACT_APP_API_URL}/user-profile/change-password`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              newPassword: formData.newPassword,
              confirmPassword: formData.confirmPassword,
            }),
          }
        );

        const passwordData = await passwordResponse.json();

        if (!passwordResponse.ok) {
          showErrorToast(passwordData.message || "Failed to update password");
          return;
        }

        // ✅ Password updated successfully → reset fields
        setFormData({
          ...formData,
          newPassword: "",
          confirmPassword: "",
        });

        showSuccessToast("Password changed successfully");
      }

      // Final success for other updates
      // showSuccessToast("Profile updated successfully");
      setIsEditing(false);
    } catch (err) {
      console.error("Save error:", err);
      showErrorToast("Failed to save changes");
    }
  };

  const handleLogout = () => {
    // ✅ local + session madhe stored token/user remove kara
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    sessionStorage.removeItem("token");
    sessionStorage.removeItem("user");

    // ✅ Redux / UI reset
    dispatch(resetSidebarState());

    // ✅ login page la redirect
    navigate("/");
  };

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    const token1 = sessionStorage.getItem("access_token");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user-profile/profile`,
        { headers: { Authorization: `Bearer ${token1}` } }
      );
      if (!res.ok) throw new Error("Failed to fetch profile");

      const data = await res.json();

      // console.log(data)

      setUser(data);

      // ✅ Update only name and profile pic in localStorage & sessionStorage
      const storedUserLocal = JSON.parse(localStorage.getItem("user")) || {};
      const storedUserSession =
        JSON.parse(sessionStorage.getItem("user")) || {};

      const updatedUser = {
        ...storedUserLocal,
        user_name: data.user_name,
        user_profile_pic: data.user_profile_pic,
      };

      // console.log(data.user_name,"jhghdgdsk")

      localStorage.setItem("user", JSON.stringify(updatedUser));
      sessionStorage.setItem(
        "user",
        JSON.stringify({
          ...storedUserSession,
          user_name: data.user_name,
          user_profile_pic: data.user_profile_pic,
        })
      );

      // console.log(localStorage.getItem("user"))

      // ✅ Trigger refresh for Topbar
      dispatch(triggerRefresh());
    } catch (err) {
      console.error(err);
      showErrorToast(TOAST_MESSAGES.PROFILE.FETCH_FAILED);
    } finally {
      setLoading(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (file.size > 1024 * 1024) {
      showErrorToast(TOAST_MESSAGES.PROFILE.IMAGE_TOO_LARGE);
      return;
    }

    const formData = new FormData();
    formData.append("profileImage", file);

    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/user-profile/upload-profile-image`,
        {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );
      if (!res.ok) throw new Error("Image upload failed");

      const data = await res.json();

      setUser((prevUser) => {
        const updatedUser = {
          ...(prevUser || {}),
          user_profile_pic:
            data?.profileImagePath || prevUser?.user_profile_pic || "",
        };

        // ✅ Update localStorage & sessionStorage
        const storedUserLocal = JSON.parse(localStorage.getItem("user")) || {};
        const storedUserSession =
          JSON.parse(sessionStorage.getItem("user")) || {};

        const updatedStoredUserLocal = {
          ...storedUserLocal,
          user_profile_pic: updatedUser.user_profile_pic,
        };
        localStorage.setItem("user", JSON.stringify(updatedStoredUserLocal));

        const updatedStoredUserSession = {
          ...storedUserSession,
          user_profile_pic: updatedUser.user_profile_pic,
        };
        sessionStorage.setItem(
          "user",
          JSON.stringify(updatedStoredUserSession)
        );

        return updatedUser;
      });

      // ✅ Trigger refresh for Topbar
      dispatch(triggerRefresh());
      showSuccessToast(TOAST_MESSAGES.PROFILE.IMAGE_UPLOAD_SUCCESS);
    } catch (err) {
      console.error("Upload error:", err);
      showErrorToast(TOAST_MESSAGES.PROFILE.IMAGE_UPLOAD_FAILED);
    }
  };

  const formatTimeAgo = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 60000); // minutes

    if (diff < 1) return "Just now";
    if (diff < 60) return `${diff} min ago`;
    if (diff < 1440) return `${Math.floor(diff / 60)} hr ago`;
    return `${Math.floor(diff / 1440)} days ago`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border textblack" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) return <div className="container py-5 text-danger">{error}</div>;

  const profileImage = user?.user_profile_pic
    ? `${SERVER_URL}/${user.user_profile_pic.replace(/\\/g, "/")}`
    : null;

  return (
    <div
      className="container-fluid profile-container"
      style={{ paddingBottom: "20px" }}
    >
      {!isEditing ? (
        <div className="card profile p-4 rounded-4 border-1 mb-1">
          <div className="d-flex justify-content-between align-items-start mb-3">
            <h4
              className="fw-bold mb-0 refresh-prompts"
              style={{ fontSize: "large" }}
            >
              User Profile
            </h4>
            <img
              src={EditIcon}
              alt="Location"
              className="me-1 icon-image"
              style={{ fontSize: "18px", cursor: "pointer" }}
              onClick={() => setIsEditing(true)}
            />
          </div>

          <div className="d-flex flex-column flex-md-row align-items-center gap-4">
            {/* Profile Image */}
            {user.user_profile_pic ? (
              <img
                src={`${SERVER_URL}/${user.user_profile_pic.replace(
                  /\\/g,
                  "/"
                )}`}
                alt="Profile"
                className="rounded-circle profile-image"
                style={{
                  width: "80px",
                  height: "80px",
                  objectFit: "cover",
                  cursor: "pointer",
                }}
              />
            ) : (
              <div
                className="rounded-circle bg-dark text-white d-flex align-items-center justify-content-center"
                style={{
                  width: "80px",
                  height: "80px",
                  fontSize: "28px",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                {user.user_name?.charAt(0).toUpperCase() || "U"}
              </div>
            )}

            {/* Info Section */}
            <div className="w-100">
              <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-between mb-3 info-section">
                <div>
                  <h5 className="mb-1 fw-semibold fs-6">
                    {user.user_name
                      ? user.user_name.charAt(0).toUpperCase() +
                        user.user_name.slice(1)
                      : "—" || "Unnamed User"}
                  </h5>
                  <p
                    className="mb-0 refresh-prompts"
                    style={{ fontSize: "14px" }}
                  >
                    Last Login:{" "}
                    {user.last_login
                      ? new Date(user.last_login).toLocaleDateString("en-US", {
                          month: "2-digit",
                          day: "2-digit",
                          year: "numeric",
                        })
                      : "N/A"}
                  </p>
                </div>
              </div>

              <div
                className="d-flex flex-wrap align-items-center justify-content-start text-muted gap-5 info-details"
                style={{ fontSize: "15px" }}
              >
                {/* <div className="d-flex refresh-prompts align-items-center">
                  <img
                    src={LocationIcon}
                    alt="Location"
                    className="me-2 profile-i"
                  />
                  {user.country || "India"}
                </div>
                <div className="d-flex refresh-prompts align-items-center">
                  <img src={EmailIcon} alt="Email" className="me-2 profile-i" />
                  {user.user_email || "John123@gmail.com"}
                </div>
                <div className="d-flex  refresh-prompts align-items-center">
                  <img
                    src={EnterpriseIcon}
                    alt="Enterprize"
                    className="me-2 profile-i"
                  />
                  {user.user_enterprise || "NextAstra"}
                </div>
                <div className="d-flex refresh-prompts align-items-center">
                  <img
                    src={UserIcon}
                    alt="User Role"
                    className="me-2 profile-i"
                  />
                  {user.role_name || "User"}
                </div> */}
                <div className="d-flex refresh-prompts align-items-center">
                  <i className="bi bi-geo-alt profile-i me-2 fs-5"></i>
                  {user.country || "India"}
                </div>

                <div className="d-flex refresh-prompts align-items-center">
                  <i className="bi bi-envelope profile-i me-2 fs-5"></i>
                  {user.user_email || "John123@gmail.com"}
                </div>

                <div className="d-flex refresh-prompts align-items-center">
                  <i className="bi bi-building profile-i me-2 fs-5"></i>
                  {user.user_enterprise || "NextAstra"}
                </div>

                <div className="d-flex refresh-prompts align-items-center">
                  <i className="bi bi-person-badge profile-i me-2 fs-5"></i>
                  {user.role_name || "User"}
                </div>

                <div className="d-flex align-items-center">
                  <span
                    className={`me-2 ${
                      user.user_status === "1" ? "text-success" : "text-danger"
                    }`}
                    style={{ fontSize: "20px" }}
                  >
                    ●
                  </span>
                  <span
                    className={
                      user.user_status === "1"
                        ? "text-success fw-bold"
                        : "text-danger fw-bold"
                    }
                  >
                    {user.user_status === "1" ? "Active" : "Inactive"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="card profile p-4 rounded-4 border-1">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h4 className="fw-bold mb-0" style={{ fontSize: "22px" }}>
              Edit Personal Details
            </h4>
            <span
              onClick={() => setIsEditing(false)}
              style={{
                cursor: "pointer",
                fontSize: "24px",
                lineHeight: 1,
              }}
              title="Close"
            >
              <img src={DiscardIcon} className="profile-i" alt="Close" />
            </span>
          </div>
          <div className="row">
            {/* Profile Picture Section (Left) */}
            <div className="col-md-4 d-flex flex-column align-items-center justify-content-center">
              <div className="position-relative mb-3">
                {selectedImage || profileImage ? (
                  // ✅ If profile picture exists → show image
                  <img
                    src={selectedImage || profileImage}
                    alt="Profile"
                    className="rounded-circle border"
                    style={{
                      width: "140px",
                      height: "140px",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  // ✅ If no profile picture → show first name initial
                  <div
                    className="rounded-circle border d-flex align-items-center justify-content-center bg-dark text-white"
                    style={{
                      width: "140px",
                      height: "140px",
                      fontSize: "48px",
                      fontWeight: "600",
                    }}
                  >
                    {user?.user_name?.charAt(0)?.toUpperCase() || "U"}
                  </div>
                )}

                {/* Upload icon overlay */}
                <label
                  htmlFor="profilePicUpload"
                  className="profile-pic-upload-icon position-absolute bottom-0 start-50 translate-middle-x bg-white border rounded-circle d-flex align-items-center justify-content-center"
                  style={{
                    transform: "translate(-50%, 50%)",
                    cursor: "pointer",
                    width: "40px",
                    height: "40px",
                  }}
                >
                  <img
                    src={CameraIcon}
                    alt="Camera"
                    style={{ width: "20px", height: "20px" }}
                  />
                </label>
              </div>

              {/* CHOOSE FILE Button */}
              <label
                htmlFor="profilePicUpload"
                className="btn custom-btn fw-bold px-4 py-2 mb-2 rounded-pill"
                style={{ fontSize: "16px", borderWidth: "2px" }}
              >
                CHOOSE FILE
              </label>
              <input
                id="profilePicUpload"
                type="file"
                className="d-none"
                onChange={handleImageUpload}
              />

              {/* File Info */}
              <div
                className="refresh-prompts text-center mt-1"
                style={{ fontSize: "14px" }}
              >
                No file selected <br />
                Maximum image size is <strong>1 MB</strong>
              </div>
            </div>

            {/* Right side - Personal details */}
            <div className="col-md-8 mt-4 mt-md-0 personal-details">
              <div className="row">
                {/* First Name (Editable) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Name
                    <span className="text-danger"> *</span>
                  </label>
                  <div className="position-relative">
                    <input
                      ref={userNameInputRef}
                      type="text"
                      className={`form-control rounded-3 ps-3 pe-5 ${
                        (!user.user_name?.trim() && isEditingUserName) ||
                        nameError
                          ? "is-invalid"
                          : ""
                      }`}
                      value={user.user_name || ""}
                      readOnly={!isEditingUserName}
                      onClick={() => setIsEditingUserName(true)}
                      onChange={(e) => {
                        const value = e.target.value;

                        // ✅ Prevent invalid characters in real-time
                        const validPattern = /^[A-Za-z\s]*$/;
                        if (!validPattern.test(value)) {
                          showErrorToast(
                            "Name can only contain letters and spaces."
                          );
                          return;
                        }

                        // ✅ Set value if valid
                        setUser({ ...user, user_name: value });
                        setNameError(false);
                      }}
                      onBlur={async () => {
                        if (!isEditingUserName) return;

                        const name = user.user_name?.trim() || "";
                        const nameRegex = /^[A-Za-z\s]{2,50}$/;

                        // ✅ Check for empty
                        if (!name) {
                          setNameError(true);
                          showErrorToast("User name cannot be blank.");
                          setIsEditingUserName(false);
                          return;
                        }

                        // ✅ Check format
                        if (!nameRegex.test(name)) {
                          setNameError(true);
                          showErrorToast(
                            "Please enter a valid name (only letters and spaces, 2–50 characters)."
                          );
                          setIsEditingUserName(false);
                          return;
                        }

                        const storedUser =
                          JSON.parse(localStorage.getItem("user")) ||
                          JSON.parse(sessionStorage.getItem("user")) ||
                          {};

                        // ✅ Skip if unchanged
                        if (name === storedUser.user_name) {
                          setIsEditingUserName(false);
                          return;
                        }

                        try {
                          const res = await fetch(
                            `${process.env.REACT_APP_API_URL}/user-profile/update`,
                            {
                              method: "PUT",
                              headers: {
                                "Content-Type": "application/json",
                                Authorization: `Bearer ${token}`,
                              },
                              body: JSON.stringify({ user_name: name }),
                            }
                          );

                          const data = await res.json();

                          if (!res.ok) {
                            showErrorToast(
                              data.message || "Failed to update user name."
                            );
                          } else {
                            showSuccessToast("User name updated successfully.");

                            // ✅ Update local/session storage
                            const updatedStoredUser = {
                              ...storedUser,
                              user_name: name,
                            };
                            localStorage.setItem(
                              "user",
                              JSON.stringify(updatedStoredUser)
                            );
                            sessionStorage.setItem(
                              "user",
                              JSON.stringify(updatedStoredUser)
                            );

                            dispatch(triggerRefresh());
                          }
                        } catch (err) {
                          console.error("Update error:", err);
                          showErrorToast("Failed to update user name.");
                        } finally {
                          setIsEditingUserName(false);
                        }
                      }}
                      style={{
                        cursor: !isEditingUserName ? "pointer" : "text",
                      }}
                    />

                    {/* 🟢 Show Edit Icon only if no error and not editing */}
                    {user.user_name?.trim() &&
                      !nameError &&
                      !isEditingUserName && (
                        <img
                          src={EditIcon}
                          alt="edit"
                          className="position-absolute profile-i top-50 end-0 translate-middle-y me-3"
                          style={{ width: "16px", cursor: "pointer" }}
                          onClick={() => setIsEditingUserName(true)}
                        />
                      )}

                    {/* Validation Feedback */}
                    {((!user.user_name?.trim() && isEditingUserName) ||
                      nameError) && (
                      <div className="invalid-feedback">
                        Please enter a valid name (only letters and spaces
                        allowed).
                      </div>
                    )}
                  </div>
                </div>

                {/* Email (Read-only) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control rounded-3 ps-3 pe-5"
                      value={user.user_email}
                      readOnly
                    />
                    {/* <img
                      src={EditIcon}
                      alt="edit"
                      className="position-absolute top-50 end-0 profile-i translate-middle-y me-3 "
                      style={{ width: "16px", pointerEvents: "none" }}
                    /> */}
                  </div>
                </div>

                {/* Enterprize (Read-only) */}
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold">
                    Enterprize Name
                  </label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control rounded-3 ps-3 pe-5"
                      value={user.user_enterprise}
                      readOnly
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="row gx-3 gy-1">
        <div className="col-12 col-md-6 mb-4 security-card">
          <div className="card profile shadow-sm p-4 h-100">
            <h5 style={{ fontSize: "large" }} className="mb-4 fw-semibold">
              Security
            </h5>

            {/* New Password */}
            <div className="mb-4">
              <label className="form-label fw-semibold">New Password</label>
              <div className="input-group">
                <input
                  type={passwordVisibility.newPassword ? "text" : "password"}
                  name="newPassword"
                  className="form-control"
                  placeholder="Enter new password"
                  value={formData.newPassword}
                  onChange={handleChange}
                />
                <span
                  className="input-group-text cursor-pointer"
                  onClick={() => togglePasswordVisibility("newPassword")}
                >
                  {passwordVisibility.newPassword ? <FaEyeSlash /> : <FaEye />}
                </span>
              </div>
            </div>

            {/* Confirm Password */}
            <div className="mb-2">
              <label className="form-label fw-semibold">Confirm Password</label>
              <div className="input-group">
                <input
                  type={
                    passwordVisibility.confirmPassword ? "text" : "password"
                  }
                  name="confirmPassword"
                  className="form-control"
                  placeholder="Re-enter new password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                />
                <span
                  className="input-group-text cursor-pointer"
                  onClick={() => togglePasswordVisibility("confirmPassword")}
                >
                  {passwordVisibility.confirmPassword ? (
                    <FaEyeSlash />
                  ) : (
                    <FaEye />
                  )}
                </span>
              </div>
            </div>

            {/* Buttons */}
            <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-3 mt-5 save-actions">
              {/* Discard Button */}
              <button
                className="rounded-5 px-5 py-2 d-flex align-items-center justify-content-center"
                style={{
                  border: "1px solid #ccc",
                  backgroundColor: "#f8f9fa",
                  color: "#333",
                  transition: "0.2s",
                }}
                onClick={() => {
                  // Reset password fields only
                  setFormData({
                    ...formData,
                    newPassword: "",
                    confirmPassword: "",
                  });
                  setIsEditing(false);
                  showInfoToast("Changes discarded");
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#e9ecef")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#f8f9fa")
                }
              >
                Cancel
              </button>

              {/* Save Button */}
              <button
                className="rounded-5 custom-btn py-2.5 d-flex align-items-center justify-content-center"
                onClick={handleSave}
                style={{
                  border: "none",
                  backgroundColor: "#007bff",
                  color: "white",
                  transition: "0.2s",
                  paddingLeft: "3.5rem",
                  paddingRight: "3.5rem",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "#0069d9")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "#007bff")
                }
              >
                Save
              </button>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-6 mb-4">
          <div className="card profile shadow-sm p-4 h-100">
            {/* Title & Description */}
            <h5 style={{ fontSize: "large" }} className="fw-semibold mb-1">
              Login Activity
            </h5>
            <p className="mb-3" style={{ fontSize: "0.9rem" }}>
              View recent user activity, device info, and last login sessions.
            </p>

            <hr className="my-2" />

            {/* Table Wrapper */}
            <div
              className="table-responsive"
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                overflowX: "auto",
                borderRadius: "8px",
              }}
            >
              <table className="table table-sm table-hover align-middle mb-0">
                {/* Table Head */}
                <thead
                  className="table-light"
                  style={{
                    position: "sticky",
                    top: 0,
                    zIndex: 10,
                  }}
                >
                  <tr>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Device
                    </th>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>IP</th>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Last Active
                    </th>
                    <th style={{ fontWeight: 600, fontSize: "0.9rem" }}>
                      Status
                    </th>
                    <th
                      style={{ fontWeight: 600, fontSize: "0.9rem" }}
                      className="text-center"
                    >
                      Action
                    </th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {/* Show Loader */}
                  {loading ? (
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        Loading activity...
                      </td>
                    </tr>
                  ) : activitySessions.length > 0 ? (
                    activitySessions.map((session) => (
                      <tr key={session.session_id}>
                        {/* Device Info */}
                        <td style={{ fontSize: "0.88rem" }}>
                          {session.device_info || session.device || "Unknown"}
                        </td>

                        {/* IP Address */}
                        <td style={{ fontSize: "0.88rem" }}>
                          {session.ip_address || "N/A"}
                        </td>

                        {/* Last Active */}
                        <td style={{ fontSize: "0.88rem" }}>
                          {new Date(session.created_timestamp).toLocaleString()}
                        </td>

                        {/* Status Badge */}
                        <td>
                          <span
                            className={`badge ${
                              session.is_active
                                ? "bg-success text-white"
                                : "bg-secondary text-white"
                            }`}
                            style={{
                              fontSize: "0.75rem",
                              padding: "6px 10px",
                              borderRadius: "6px",
                            }}
                          >
                            {session.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Action Button */}
                        <td className="text-center">
                          {session.is_active ? (
                            <button
                              onClick={() =>
                                handleLogoutDevice(session.session_id)
                              }
                              className="btn btn-sm btn-outline-danger d-flex align-items-center justify-content-center"
                              style={{
                                padding: "2px 8px",
                                borderRadius: "6px",
                                fontSize: "0.8rem",
                              }}
                            >
                              Logout
                              <img
                                src={RedLogoutIcon}
                                alt="Logout"
                                className="ms-2"
                                style={{ width: "14px" }}
                              />
                            </button>
                          ) : (
                            <span
                              className="text-muted"
                              style={{ fontSize: "0.8rem" }}
                            >
                              —
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    // No Sessions Found
                    <tr>
                      <td colSpan="5" className="text-center text-muted py-3">
                        No login sessions found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserProfileSettings;
